import React, { useState, useEffect, useRef } from 'react';
import { FaFilePdf, FaTimes, FaDownload, FaPrint, FaSpinner, FaUser, FaChartLine, FaWallet, FaCreditCard, FaShieldAlt, FaPiggyBank, FaFileAlt, FaLightbulb, FaClipboardList, FaBrain } from 'react-icons/fa';
import axios from 'axios';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Legend } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Format currency helper
const formatCurrency = (value) => {
  if (!value && value !== 0) return '₹0';
  const numValue = Math.round(value);
  if (numValue >= 10000000) return `₹${(numValue / 10000000).toFixed(2)}Cr`;
  if (numValue >= 100000) return `₹${(numValue / 100000).toFixed(2)}L`;
  return `₹${numValue.toLocaleString('en-IN')}`;
};

// Format AI insights - handles both string and structured object formats
const formatAIInsights = (insights) => {
  if (!insights) return null;
  
  // If it's already a string, return as-is
  if (typeof insights === 'string') return insights;
  
  // If it's an array of insight objects
  if (Array.isArray(insights)) {
    return insights.map((item, idx) => {
      if (typeof item === 'string') return item;
      if (typeof item === 'object' && item.message) {
        return `${item.title || ''}: ${item.message}`;
      }
      return JSON.stringify(item);
    }).join('\n');
  }
  
  // If it's an object with summary/insights/action_points
  if (typeof insights === 'object') {
    const parts = [];
    if (insights.summary) parts.push(insights.summary);
    if (insights.insights && Array.isArray(insights.insights)) {
      insights.insights.forEach(item => {
        if (typeof item === 'string') parts.push(`• ${item}`);
        else if (item.message) parts.push(`• ${item.title || ''}: ${item.message}`);
      });
    }
    if (insights.action_points && Array.isArray(insights.action_points)) {
      parts.push('\nAction Points:');
      insights.action_points.forEach(ap => parts.push(`• ${ap}`));
    }
    return parts.join('\n');
  }
  
  return String(insights);
};

// Risk Profiler Questions
const riskQuestions = [
  {
    id: 'q1',
    question: '1. What is your primary goal for this investment?',
    options: [
      { value: 0, label: 'Safety of Capital' },
      { value: 3, label: 'Balance of Safety & Growth' },
      { value: 5, label: 'Maximize Long-term Growth' }
    ]
  },
  {
    id: 'q2',
    question: '2. If your portfolio drops 20% in a month, what would you do?',
    options: [
      { value: 0, label: 'Sell immediately to prevent loss' },
      { value: 3, label: 'Hold and wait for recovery' },
      { value: 5, label: 'Buy more at lower prices' }
    ]
  },
  {
    id: 'q3',
    question: '3. How many years until you need this money?',
    options: [
      { value: 0, label: 'Less than 3 years' },
      { value: 3, label: '3 to 7 years' },
      { value: 5, label: 'More than 7 years' }
    ]
  },
  {
    id: 'q4',
    question: '4. What is your experience with equity markets?',
    options: [
      { value: 0, label: 'No experience / FD only' },
      { value: 3, label: 'Some mutual funds' },
      { value: 5, label: 'Active stock investor' }
    ]
  }
];

// COLORS for charts
const COLORS = ['#FFD700', '#1E3A5F', '#22C55E', '#EF4444', '#3B82F6', '#A855F7'];

// Section Header Component - Consistent styling for all sections
const SectionHeader = ({ icon: Icon, title, subtitle }) => (
  <div className="bg-gradient-to-r from-[#1A2942] to-[#1E3A5F] p-4 border-b-4 border-[#D4AF37]">
    <div className="flex items-center space-x-3">
      {Icon && <Icon className="text-[#FFD700] text-xl" />}
      <div>
        <h2 className="text-xl font-bold text-[#FFD700]">{title}</h2>
        {subtitle && <p className="text-gray-400 text-sm">{subtitle}</p>}
      </div>
    </div>
  </div>
);

// PDF Report Preview Component
const PDFReportPreview = ({ clientId, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [data, setData] = useState(null);
  const [branding, setBranding] = useState({});
  const [aiInsights, setAiInsights] = useState({});
  const reportRef = useRef(null);

  useEffect(() => {
    fetchAllData();
  }, [clientId]);

  const fetchAllData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [
        clientRes, riskRes, investmentsRes, liabilitiesRes, insuranceRes,
        educationRes, marriageRes, retirementRes, customRes, netWorthRes,
        brandingRes, nextStepsRes
      ] = await Promise.all([
        axios.get(`${API}/clients/${clientId}`, { headers }),
        axios.get(`${API}/risk-assessment/${clientId}`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/investments/${clientId}`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/liabilities/${clientId}`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/insurance-analysis/${clientId}`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/education-goals/${clientId}`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/marriage-goals/${clientId}`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/retirement-goals/${clientId}`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/custom-goals/${clientId}`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/net-worth/${clientId}`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/advisor-branding`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API}/next-steps-feedback/${clientId}`, { headers }).catch(() => ({ data: null }))
      ]);

      const fetchedData = {
        client: clientRes.data,
        riskProfile: riskRes.data,
        investments: Array.isArray(investmentsRes.data) ? investmentsRes.data : [],
        liabilities: Array.isArray(liabilitiesRes.data) ? liabilitiesRes.data : [],
        insurance: insuranceRes.data,
        educationGoals: Array.isArray(educationRes.data) ? educationRes.data : [],
        marriageGoals: Array.isArray(marriageRes.data) ? marriageRes.data : [],
        retirementGoal: retirementRes.data,
        customGoals: Array.isArray(customRes.data) ? customRes.data : [],
        netWorth: netWorthRes.data,
        nextSteps: nextStepsRes.data
      };

      setData(fetchedData);
      setBranding(brandingRes.data || {});

      // Fetch AI insights
      await fetchAIInsights(fetchedData, headers);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchAIInsights = async (fetchedData, headers) => {
    const insights = {};
    
    // Risk Profile AI Insight
    if (fetchedData.riskProfile?.ai_recommendation) {
      insights.risk = fetchedData.riskProfile.ai_recommendation;
    }

    // Investment AI Insight
    if (fetchedData.investments?.length > 0) {
      try {
        const totalInvested = fetchedData.investments.reduce((sum, inv) => sum + (inv.amount_invested || 0), 0);
        const totalCurrentValue = fetchedData.investments.reduce((sum, inv) => sum + (inv.current_value || 0), 0);
        const response = await axios.post(`${API}/net-worth/ai-analysis`, {
          analysis_type: 'investments',
          data: {
            total_investments: fetchedData.investments.length,
            total_invested: totalInvested,
            total_current_value: totalCurrentValue,
            overall_gain_loss: totalCurrentValue - totalInvested
          }
        }, { headers }).catch(() => null);
        if (response?.data) {
          // Handle both string and structured response formats
          insights.investments = response.data.analysis || response.data.summary || response.data;
        }
      } catch (e) {
      }
    }

    // Liability AI Insight
    if (fetchedData.liabilities?.length > 0) {
      try {
        const totalDebt = fetchedData.liabilities.reduce((sum, l) => sum + (l.principal_outstanding || 0), 0);
        const response = await axios.post(`${API}/net-worth/ai-analysis`, {
          analysis_type: 'liabilities',
          data: {
            total_loans: fetchedData.liabilities.length,
            total_debt: totalDebt
          }
        }, { headers }).catch(() => null);
        if (response?.data) {
          // Handle both string and structured response formats
          insights.liabilities = response.data.analysis || response.data.summary || response.data;
        }
      } catch (e) {
      }
    }

    // Insurance AI Insight - fetch from separate endpoint if not available
    if (fetchedData.insurance) {
      if (fetchedData.insurance.ai_recommendation) {
        insights.insurance = fetchedData.insurance.ai_recommendation;
      } else {
        // Fetch AI insights for insurance
        try {
          const response = await axios.get(`${API}/insurance-analysis/${fetchedData.client?.id}/ai-insights`, { headers }).catch(() => null);
          if (response?.data?.ai_recommendation) {
            insights.insurance = response.data.ai_recommendation;
          } else {
            // Generate on the fly if not available
            const aiResponse = await axios.post(`${API}/net-worth/ai-analysis`, {
              analysis_type: 'insurance',
              data: {
                hlv_value: fetchedData.insurance.hlv_value,
                required_cover: fetchedData.insurance.required_cover,
                existing_cover: fetchedData.insurance.existing_life_cover,
                protection_gap: fetchedData.insurance.protection_gap,
                health_gap: fetchedData.insurance.health_gap,
                emergency_fund_gap: fetchedData.insurance.emergency_fund_gap
              }
            }, { headers }).catch(() => null);
            if (aiResponse?.data) {
              // Handle both string and structured response formats
              insights.insurance = aiResponse.data.analysis || aiResponse.data.summary || aiResponse.data;
            }
          }
        } catch (e) {
        }
      }
    }

    // Net Worth AI Insight
    if (fetchedData.netWorth) {
      if (fetchedData.netWorth.ai_analysis) {
        insights.netWorth = fetchedData.netWorth.ai_analysis;
      } else {
        try {
          const response = await axios.post(`${API}/net-worth/ai-analysis`, {
            analysis_type: 'net_worth',
            data: {
              total_assets: fetchedData.netWorth.total_assets,
              total_liabilities: fetchedData.netWorth.total_liabilities,
              net_worth: fetchedData.netWorth.net_worth,
              debt_to_asset_ratio: fetchedData.netWorth.debt_to_asset_ratio,
              liquidity_ratio: fetchedData.netWorth.liquidity_ratio
            }
          }, { headers }).catch(() => null);
          if (response?.data) {
            // Handle both string and structured response formats
            insights.netWorth = response.data.analysis || response.data.summary || response.data;
          }
        } catch (e) {
        }
      }
    }

    setAiInsights(insights);
  };

  const generatePDF = async () => {
    setGenerating(true);
    try {
      const reportElement = reportRef.current;
      if (!reportElement) return;

      // Capture full scrollable content
      const canvas = await html2canvas(reportElement, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#0F172A',
        logging: false,
        width: reportElement.scrollWidth,
        height: reportElement.scrollHeight,
        windowWidth: reportElement.scrollWidth,
        windowHeight: reportElement.scrollHeight,
        scrollX: 0,
        scrollY: 0
      });

      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = pdfWidth / imgWidth;
      const scaledHeight = imgHeight * ratio;
      
      let heightLeft = scaledHeight;
      let position = 0;
      let pageNumber = 1;

      // Add pages as needed
      while (heightLeft > 0) {
        if (pageNumber > 1) {
          pdf.addPage();
        }
        
        pdf.addImage(imgData, 'JPEG', 0, position, pdfWidth, scaledHeight);
        heightLeft -= pdfHeight;
        position -= pdfHeight;
        pageNumber++;
      }

      pdf.save(`${data.client?.name || 'Client'}_Financial_Report.pdf`);
    } catch (error) {
      alert('PDF generation failed. Please try Print instead.');
    } finally {
      setGenerating(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
        <div className="text-center">
          <FaSpinner className="animate-spin text-[#FFD700] text-4xl mx-auto mb-4" />
          <p className="text-white">Loading report data...</p>
        </div>
      </div>
    );
  }

  // Calculate totals
  const totalInvested = data?.investments?.reduce((sum, inv) => sum + (inv.amount_invested || 0), 0) || 0;
  const totalCurrentValue = data?.investments?.reduce((sum, inv) => sum + (inv.current_value || 0), 0) || 0;
  const totalGainLoss = totalCurrentValue - totalInvested;
  const totalDebt = data?.liabilities?.reduce((sum, l) => sum + (l.principal_outstanding || 0), 0) || 0;
  const totalUnsecuredDebt = data?.liabilities?.filter(l => !l.is_insured).reduce((sum, l) => sum + (l.principal_outstanding || 0), 0) || 0;

  // Get all goals with proper field mapping
  const allGoals = [
    ...(data?.educationGoals || []).map(g => ({ 
      ...g, 
      type: 'Education', 
      icon: '🎓',
      // Map API fields to display fields
      future_value: g.future_goal_value || g.future_value || 0,
      monthly_sip: g.required_monthly_sip || g.monthly_sip || 0,
      expected_return: g.expected_return_percent || g.expected_return || 12,
      time_horizon: g.time_horizon_years || g.time_horizon || 0,
      current_corpus: g.existing_corpus || g.current_corpus || 0,
      life_insurance_needed: g.life_insurance_required || g.life_insurance_needed || g.future_goal_value || 0,
      target_age: g.goal_age || g.target_age
    })),
    ...(data?.marriageGoals || []).map(g => ({ 
      ...g, 
      type: 'Marriage', 
      icon: '💒',
      future_value: g.future_goal_value || g.future_value || 0,
      monthly_sip: g.required_monthly_sip || g.monthly_sip || 0,
      expected_return: g.expected_return_percent || g.expected_return || 12,
      time_horizon: g.time_horizon_years || g.time_horizon || 0,
      current_corpus: g.existing_corpus || g.current_corpus || 0,
      life_insurance_needed: g.life_insurance_required || g.life_insurance_needed || g.future_goal_value || 0,
      target_age: g.goal_age || g.target_age
    })),
    ...(data?.retirementGoal ? [{ 
      ...data.retirementGoal, 
      type: 'Retirement', 
      icon: '🏖️',
      future_value: data.retirementGoal.target_corpus || data.retirementGoal.future_value || 0,
      monthly_sip: data.retirementGoal.required_monthly_sip || data.retirementGoal.monthly_sip || 0,
      expected_return: data.retirementGoal.expected_return_percent || data.retirementGoal.expected_return || 12,
      time_horizon: data.retirementGoal.years_to_retirement || data.retirementGoal.time_horizon || 0,
      current_corpus: data.retirementGoal.existing_corpus || data.retirementGoal.current_corpus || 0,
      life_insurance_needed: data.retirementGoal.life_insurance_required || data.retirementGoal.future_value || 0
    }] : []),
    ...(data?.customGoals || []).map(g => ({ 
      ...g, 
      type: 'Custom', 
      icon: '🎯',
      future_value: g.future_goal_value || g.future_value || 0,
      monthly_sip: g.required_monthly_sip || g.monthly_sip || 0,
      expected_return: g.expected_return_percent || g.expected_return || 12,
      time_horizon: g.time_horizon_years || g.time_horizon || 0,
      current_corpus: g.existing_corpus || g.current_corpus || 0,
      life_insurance_needed: g.life_insurance_required || g.life_insurance_needed || g.future_goal_value || 0,
      target_age: g.goal_age || g.target_age
    }))
  ];

  // Chart data
  const assetAllocationData = data?.riskProfile ? [
    { name: 'Equity', value: data.riskProfile.equity_allocation || 0 },
    { name: 'Debt', value: data.riskProfile.debt_allocation || 0 }
  ] : [];

  const insuranceGapData = data?.insurance ? [
    { name: 'Required Cover', value: data.insurance.required_cover || 0, fill: '#3B82F6' },
    { name: 'Existing Cover', value: data.insurance.existing_life_cover || 0, fill: '#22C55E' },
    { name: 'Gap', value: data.insurance.protection_gap || data.insurance.life_insurance_gap || 0, fill: '#EF4444' }
  ] : [];

  const netWorthChartData = data?.netWorth ? [
    { name: 'Assets', value: data.netWorth.total_assets || 0, fill: '#22C55E' },
    { name: 'Liabilities', value: data.netWorth.total_liabilities || 0, fill: '#EF4444' },
    { name: 'Net Worth', value: data.netWorth.net_worth || 0, fill: '#FFD700' }
  ] : [];

  return (
    <div className="fixed inset-0 bg-black/95 flex flex-col z-50 print:relative print:bg-white">
      {/* Top Action Bar - Hidden during print */}
      <div className="bg-[#1A2942] p-4 flex items-center justify-between border-b border-[#2D5A8F] flex-shrink-0 print:hidden">
        <div className="flex items-center space-x-3">
          <FaFilePdf className="text-red-500" size={24} />
          <h2 className="text-white font-bold text-lg">Report Preview - {data?.client?.name}</h2>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={handlePrint}
            className="flex items-center space-x-2 px-4 py-2 bg-[#1E3A5F] text-white rounded-lg hover:bg-[#2D5A8F] transition-colors"
            data-testid="print-report-btn"
          >
            <FaPrint />
            <span>Print Report</span>
          </button>
          <button
            onClick={generatePDF}
            disabled={generating}
            className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded-lg hover:opacity-90 transition-opacity"
            data-testid="download-pdf-btn"
          >
            {generating ? <FaSpinner className="animate-spin" /> : <FaDownload />}
            <span>{generating ? 'Generating...' : 'Download PDF'}</span>
          </button>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-white" data-testid="close-preview-btn">
            <FaTimes size={24} />
          </button>
        </div>
      </div>

      {/* Scrollable Vertical Report - Single continuous document */}
      <div className="flex-1 overflow-auto bg-slate-900 print:overflow-visible print:bg-white">
        <div 
          ref={reportRef} 
          className="max-w-4xl mx-auto bg-[#0F172A] print:bg-white print:max-w-none"
          id="report-content"
          data-testid="report-content"
        >
          {/* Report Header with Branding */}
          <div className="bg-gradient-to-r from-[#1A2942] via-[#1E3A5F] to-[#1A2942] p-6 text-center">
            <div className="flex items-center justify-center space-x-4 mb-4">
              {branding.company_logo ? (
                <img src={branding.company_logo} alt="Logo" className="w-16 h-16 rounded-lg object-cover" />
              ) : (
                <div className="w-16 h-16 bg-[#FFD700] rounded-lg flex items-center justify-center text-[#1A2942] font-bold text-2xl">
                  AK
                </div>
              )}
              <div className="text-left">
                <h1 className="text-3xl font-bold text-[#FFD700]">ARTH KUNDLI PRO</h1>
                <p className="text-gray-300">Your Personalized Financial Blueprint</p>
              </div>
            </div>
            <div className="flex justify-center space-x-8 text-sm text-gray-300">
              <span>Advisor: {branding.advisor_name || 'Financial Advisor'}</span>
              <span>|</span>
              <span>{branding.designation || 'Certified Financial Planner'}</span>
              {branding.phone && <><span>|</span><span>{branding.phone}</span></>}
            </div>
          </div>

          {/* SECTION 1: Client Profile */}
          <div className="border-b border-[#2D5A8F]">
            <SectionHeader icon={FaUser} title="CLIENT INFORMATION" subtitle="Personal & Family Details" />
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-[#1A2942] rounded-lg p-4">
                  <p className="text-gray-400 text-sm">Full Name</p>
                  <p className="text-white font-bold text-lg">{data?.client?.name || 'N/A'}</p>
                </div>
                <div className="bg-[#1A2942] rounded-lg p-4">
                  <p className="text-gray-400 text-sm">Occupation</p>
                  <p className="text-white font-bold text-lg">{data?.client?.profile_occupation || 'N/A'}</p>
                </div>
                <div className="bg-[#1A2942] rounded-lg p-4">
                  <p className="text-gray-400 text-sm">Age</p>
                  <p className="text-white font-bold text-lg">{data?.client?.age ? `${data.client.age} years` : 'N/A'}</p>
                </div>
                <div className="bg-[#1A2942] rounded-lg p-4">
                  <p className="text-gray-400 text-sm">Contact</p>
                  <p className="text-white font-bold text-lg">{data?.client?.contact || data?.client?.phone || 'N/A'}</p>
                </div>
                <div className="bg-[#1A2942] rounded-lg p-4">
                  <p className="text-gray-400 text-sm">Spouse Name</p>
                  <p className="text-white font-bold text-lg">{data?.client?.spouse_name || 'N/A'}</p>
                </div>
                <div className="bg-[#1A2942] rounded-lg p-4">
                  <p className="text-gray-400 text-sm">Total Dependents</p>
                  <p className="text-white font-bold text-lg">{data?.client?.total_dependents ?? data?.client?.dependents ?? 'N/A'}</p>
                </div>
              </div>
              
              {data?.client?.children?.length > 0 && (
                <div className="bg-[#1A2942] rounded-lg p-4">
                  <h4 className="text-[#FFD700] font-bold mb-3">Children Details</h4>
                  <div className="grid grid-cols-2 gap-3">
                    {data.client.children.map((child, idx) => (
                      <div key={idx} className="bg-[#0F172A] rounded-lg p-3 border border-[#2D5A8F]">
                        <p className="text-white font-bold">{child.name}</p>
                        <p className="text-gray-400 text-sm">Age: {child.age} years | Class: {child.current_class || 'N/A'}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* SECTION 2: Risk Profiler Assessment */}
          {data?.riskProfile && (
            <div className="border-b border-[#2D5A8F]">
              <SectionHeader icon={FaChartLine} title="RISK PROFILER ASSESSMENT" subtitle="Understanding your investment personality" />
              <div className="p-6 space-y-4">
                {/* Questions with Answers */}
                <div className="space-y-3">
                  {riskQuestions.map((q, qIdx) => {
                    const answerKeys = ['q1_investment_goal', 'q2_portfolio_drop', 'q3_time_horizon', 'q4_market_experience'];
                    const answerValue = data.riskProfile[answerKeys[qIdx]];
                    
                    return (
                      <div key={q.id} className="bg-[#1A2942] rounded-lg p-4">
                        <p className="text-white font-semibold mb-2">{q.question}</p>
                        <div className="flex flex-wrap gap-2">
                          {q.options.map((opt) => (
                            <div
                              key={opt.value}
                              className={`px-4 py-2 rounded-lg text-sm ${
                                answerValue === opt.value
                                  ? 'bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#1A2942] font-bold'
                                  : 'bg-[#0F172A] text-gray-400'
                              }`}
                            >
                              {opt.label}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Risk Profile Results */}
                <div className="bg-[#1A2942] rounded-lg p-6">
                  <h4 className="text-[#FFD700] font-bold text-xl mb-4">Risk Profile Results</h4>
                  <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div>
                        <p className="text-gray-400 text-sm">Profile Type</p>
                        <p className="text-white font-bold text-3xl">{data.riskProfile.risk_profile}</p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm mb-2">Risk Score</p>
                        <div className="flex items-center space-x-3">
                          <div className="flex-1 bg-gray-700 rounded-full h-4">
                            <div
                              className="bg-gradient-to-r from-green-500 via-yellow-500 to-red-500 h-4 rounded-full transition-all"
                              style={{ width: `${(data.riskProfile.risk_score / 20) * 100}%` }}
                            />
                          </div>
                          <span className="text-[#FFD700] font-bold text-lg">{data.riskProfile.risk_score}/20</span>
                        </div>
                      </div>
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm mb-2 text-center">Recommended Asset Allocation</p>
                      <div style={{ width: '100%', height: '160px' }}>
                        <ResponsiveContainer width="100%" height="100%" minHeight={160}>
                          <PieChart>
                            <Pie
                              data={assetAllocationData}
                              cx="50%"
                              cy="50%"
                              innerRadius={40}
                              outerRadius={60}
                              dataKey="value"
                              label={({ name, value }) => `${name}: ${value}%`}
                            >
                              <Cell fill="#FFD700" />
                              <Cell fill="#1E3A5F" />
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="flex justify-center space-x-4 mt-2">
                        <span className="flex items-center"><span className="w-3 h-3 rounded bg-[#FFD700] mr-1"></span><span className="text-gray-300 text-sm">Equity {data.riskProfile.equity_allocation}%</span></span>
                        <span className="flex items-center"><span className="w-3 h-3 rounded bg-[#1E3A5F] mr-1"></span><span className="text-gray-300 text-sm">Debt {data.riskProfile.debt_allocation}%</span></span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* AI Insights */}
                {(data.riskProfile.ai_recommendation || aiInsights.risk) && (
                  <div className="bg-[#1A2942] rounded-lg p-4 border border-[#D4AF37]">
                    <div className="flex items-center space-x-2 mb-3">
                      <FaBrain className="text-[#FFD700]" />
                      <h5 className="text-[#FFD700] font-bold">AI-Powered Insights</h5>
                    </div>
                    <p className="text-gray-300 whitespace-pre-wrap text-sm">{formatAIInsights(data.riskProfile.ai_recommendation || aiInsights.risk)}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* SECTION 3: Current Savings & Investments */}
          <div className="border-b border-[#2D5A8F]">
            <SectionHeader icon={FaWallet} title="CURRENT SAVINGS & INVESTMENTS" subtitle="Your investment portfolio mapped to goals" />
            <div className="p-6 space-y-4">
              {/* Corpus by Goal Summary */}
              {data?.investments?.length > 0 && (
                <div className="bg-[#1A2942] rounded-lg p-4">
                  <h4 className="text-[#FFD700] font-bold mb-3">Corpus by Goal</h4>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(
                      data.investments.reduce((acc, inv) => {
                        const goal = inv.mapped_goal || 'Unmapped';
                        acc[goal] = (acc[goal] || 0) + (inv.current_value || 0);
                        return acc;
                      }, {})
                    ).map(([goal, amount]) => (
                      <div key={goal} className="bg-[#0F172A] rounded-lg px-4 py-2 border border-[#2D5A8F]">
                        <p className="text-gray-400 text-xs">{goal}</p>
                        <p className="text-[#FFD700] font-bold">{formatCurrency(amount)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Investment Table */}
              <div className="bg-[#1A2942] rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-[#0F172A]">
                    <tr className="text-gray-400">
                      <th className="px-4 py-3 text-left">Instrument</th>
                      <th className="px-4 py-3 text-left">Type / Frequency</th>
                      <th className="px-4 py-3 text-right">Amount Invested</th>
                      <th className="px-4 py-3 text-right">Rate (%)</th>
                      <th className="px-4 py-3 text-right">Current Value</th>
                      <th className="px-4 py-3 text-left">Mapped Goal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.investments?.length > 0 ? (
                      data.investments.map((inv, idx) => (
                        <tr key={idx} className="border-t border-[#2D5A8F]">
                          <td className="px-4 py-3 text-white font-medium">{inv.instrument_name}</td>
                          <td className="px-4 py-3 text-white">{inv.investment_type}<br/><span className="text-gray-500 text-xs">{inv.frequency}</span></td>
                          <td className="px-4 py-3 text-white text-right">{formatCurrency(inv.amount_invested)}</td>
                          <td className="px-4 py-3 text-white text-right">{inv.rate_of_return}%</td>
                          <td className="px-4 py-3 text-[#FFD700] font-bold text-right">{formatCurrency(inv.current_value)}</td>
                          <td className="px-4 py-3 text-gray-300">{inv.mapped_goal || '-'}</td>
                        </tr>
                      ))
                    ) : (
                      <tr><td colSpan="6" className="px-4 py-8 text-center text-gray-400">No investments added yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* AI Investment Recommendations */}
              <div className="bg-[#1A2942] rounded-lg p-4 border border-[#D4AF37]">
                <div className="flex items-center space-x-2 mb-3">
                  <FaBrain className="text-[#FFD700]" />
                  <h5 className="text-[#FFD700] font-bold">AI-Powered Investment Recommendations</h5>
                </div>
                <h6 className="text-white font-semibold mb-2">Portfolio Overview</h6>
                <div className="grid grid-cols-4 gap-3 mb-4">
                  <div className="bg-[#0F172A] rounded-lg p-3">
                    <p className="text-gray-400 text-xs">Total Invested</p>
                    <p className="text-white font-bold">{formatCurrency(totalInvested)}</p>
                  </div>
                  <div className="bg-[#0F172A] rounded-lg p-3">
                    <p className="text-gray-400 text-xs">Current Value</p>
                    <p className="text-white font-bold">{formatCurrency(totalCurrentValue)}</p>
                  </div>
                  <div className="bg-[#0F172A] rounded-lg p-3">
                    <p className="text-gray-400 text-xs">Gain/Loss</p>
                    <p className={`font-bold ${totalGainLoss >= 0 ? 'text-green-400' : 'text-red-400'}`}>{formatCurrency(totalGainLoss)}</p>
                  </div>
                  <div className="bg-[#0F172A] rounded-lg p-3">
                    <p className="text-gray-400 text-xs">Return %</p>
                    <p className={`font-bold ${totalGainLoss >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {totalInvested > 0 ? ((totalGainLoss / totalInvested) * 100).toFixed(1) : 0}%
                    </p>
                  </div>
                </div>
                {aiInsights.investments && (
                  <div className="bg-[#0F172A] rounded-lg p-3 border border-[#2D5A8F]">
                    <p className="text-gray-300 whitespace-pre-wrap text-sm">{formatAIInsights(aiInsights.investments)}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* SECTION 4: Current Liabilities & Loans */}
          <div className="border-b border-[#2D5A8F]">
            <SectionHeader icon={FaCreditCard} title="CURRENT LIABILITIES & LOANS" subtitle="Track outstanding debts & insurance status" />
            <div className="p-6 space-y-4">
              {/* Liability Table */}
              <div className="bg-[#1A2942] rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-[#0F172A]">
                    <tr className="text-gray-400">
                      <th className="px-4 py-3 text-left">Loan Type</th>
                      <th className="px-4 py-3 text-left">Bank / Lender</th>
                      <th className="px-4 py-3 text-right">Principal Outstanding</th>
                      <th className="px-4 py-3 text-right">Rate (%)</th>
                      <th className="px-4 py-3 text-right">Term (Years)</th>
                      <th className="px-4 py-3 text-center">Insured?</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.liabilities?.length > 0 ? (
                      data.liabilities.map((loan, idx) => (
                        <tr key={idx} className="border-t border-[#2D5A8F]">
                          <td className="px-4 py-3 text-white font-medium">{loan.loan_type}</td>
                          <td className="px-4 py-3 text-white">{loan.bank_lender}</td>
                          <td className="px-4 py-3 text-white text-right">{formatCurrency(loan.principal_outstanding)}</td>
                          <td className="px-4 py-3 text-white text-right">{loan.interest_rate}%</td>
                          <td className="px-4 py-3 text-white text-right">{loan.term_years}</td>
                          <td className="px-4 py-3 text-center">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${loan.is_insured ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                              {loan.is_insured ? 'Yes' : 'No'}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr><td colSpan="6" className="px-4 py-8 text-center text-gray-400">No loans added yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Unsecured Debt Highlight */}
              <div className="flex justify-end">
                <div className="bg-red-500/10 border border-red-500 rounded-lg px-6 py-3">
                  <p className="text-red-400 font-bold">TOTAL UNSECURED DEBT: {formatCurrency(totalUnsecuredDebt)}</p>
                </div>
              </div>

              {/* AI Debt Recommendations */}
              <div className="bg-[#1A2942] rounded-lg p-4 border border-[#D4AF37]">
                <div className="flex items-center space-x-2 mb-3">
                  <FaBrain className="text-[#FFD700]" />
                  <h5 className="text-[#FFD700] font-bold">AI-Powered Debt Recommendations</h5>
                </div>
                <div className="grid grid-cols-5 gap-3 mb-4">
                  <div className="bg-[#0F172A] rounded-lg p-3">
                    <p className="text-gray-400 text-xs">Total Debt</p>
                    <p className="text-white font-bold">{formatCurrency(totalDebt)}</p>
                  </div>
                  <div className="bg-[#0F172A] rounded-lg p-3 border border-red-500/50">
                    <p className="text-gray-400 text-xs">Unsecured</p>
                    <p className="text-red-400 font-bold">{formatCurrency(totalUnsecuredDebt)}</p>
                  </div>
                  <div className="bg-[#0F172A] rounded-lg p-3">
                    <p className="text-gray-400 text-xs">Avg Interest</p>
                    <p className="text-white font-bold">
                      {data?.liabilities?.length > 0 ? (data.liabilities.reduce((sum, l) => sum + l.interest_rate, 0) / data.liabilities.length).toFixed(1) : 0}%
                    </p>
                  </div>
                  <div className="bg-[#0F172A] rounded-lg p-3 border border-green-500/50">
                    <p className="text-gray-400 text-xs">Insured Loans</p>
                    <p className="text-green-400 font-bold">{data?.liabilities?.filter(l => l.is_insured).length || 0}</p>
                  </div>
                  <div className="bg-[#0F172A] rounded-lg p-3 border border-red-500/50">
                    <p className="text-gray-400 text-xs">Uninsured Loans</p>
                    <p className="text-red-400 font-bold">{data?.liabilities?.filter(l => !l.is_insured).length || 0}</p>
                  </div>
                </div>
                {aiInsights.liabilities && (
                  <div className="bg-[#0F172A] rounded-lg p-3 border border-[#2D5A8F]">
                    <p className="text-gray-300 whitespace-pre-wrap text-sm">{formatAIInsights(aiInsights.liabilities)}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* SECTION 5: Income & Debt Protection (Insurance) */}
          <div className="border-b border-[#2D5A8F]">
            <SectionHeader icon={FaShieldAlt} title="INCOME & DEBT PROTECTION" subtitle="Life Insurance Gap Analysis (Human Life Value Method)" />
            <div className="p-6 space-y-4">
              {data?.insurance ? (
                <>
                  {/* Life Insurance Calculation Inputs */}
                  <div className="bg-[#1A2942] rounded-lg p-4">
                    <h4 className="text-[#FFD700] font-bold mb-3">Life Insurance Calculation Inputs</h4>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-[#0F172A] rounded-lg p-3">
                        <p className="text-gray-400 text-xs">Annual Income</p>
                        <p className="text-white font-bold">{formatCurrency(data.insurance.annual_income)}</p>
                      </div>
                      <div className="bg-[#0F172A] rounded-lg p-3">
                        <p className="text-gray-400 text-xs">Self Maintenance %</p>
                        <p className="text-white font-bold">{data.insurance.self_maintenance_percent || data.insurance.self_maintenance_percentage}%</p>
                      </div>
                      <div className="bg-[#0F172A] rounded-lg p-3">
                        <p className="text-gray-400 text-xs">Years Coverage</p>
                        <p className="text-white font-bold">{data.insurance.years_coverage} years</p>
                      </div>
                      <div className="bg-[#0F172A] rounded-lg p-3">
                        <p className="text-gray-400 text-xs">Expected Return %</p>
                        <p className="text-white font-bold">{data.insurance.expected_return_percent || data.insurance.expected_return}%</p>
                      </div>
                      <div className="bg-[#0F172A] rounded-lg p-3">
                        <p className="text-gray-400 text-xs">Inflation %</p>
                        <p className="text-white font-bold">{data.insurance.inflation_percent || data.insurance.inflation_rate}%</p>
                      </div>
                      <div className="bg-[#0F172A] rounded-lg p-3">
                        <p className="text-gray-400 text-xs">Outstanding Debts</p>
                        <p className="text-white font-bold">{formatCurrency(data.insurance.outstanding_debts)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Life Insurance Gap Analysis with Chart */}
                  <div className="bg-[#1A2942] rounded-lg p-4">
                    <h4 className="text-blue-400 font-bold mb-3">Life Insurance Gap Analysis</h4>
                    <div className="grid grid-cols-2 gap-6">
                      <div className="space-y-3">
                        <div className="bg-[#0F172A] rounded-lg p-4">
                          <p className="text-gray-400 text-sm">HLV Value (Human Life Value)</p>
                          <p className="text-white font-bold text-2xl">{formatCurrency(data.insurance.hlv_value)}</p>
                        </div>
                        <div className="bg-[#0F172A] rounded-lg p-4">
                          <p className="text-gray-400 text-sm">Required Cover</p>
                          <p className="text-blue-400 font-bold text-2xl">{formatCurrency(data.insurance.required_cover)}</p>
                        </div>
                        <div className="bg-[#0F172A] rounded-lg p-4">
                          <p className="text-gray-400 text-sm">Existing Cover</p>
                          <p className="text-green-400 font-bold text-2xl">{formatCurrency(data.insurance.existing_life_cover || 0)}</p>
                        </div>
                        <div className="bg-[#0F172A] rounded-lg p-4 border-2 border-red-500">
                          <p className="text-gray-400 text-sm">Protection Gap</p>
                          <p className="text-red-400 font-bold text-2xl">{formatCurrency(data.insurance.protection_gap)}</p>
                          {data.insurance.protection_gap > 0 && (
                            <p className="text-red-400 text-xs mt-1">⚠️ Critical Gap: Immediate action required</p>
                          )}
                        </div>
                      </div>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={insuranceGapData} layout="vertical">
                            <XAxis type="number" tickFormatter={(v) => formatCurrency(v)} />
                            <YAxis type="category" dataKey="name" width={100} />
                            <Tooltip formatter={(v) => formatCurrency(v)} />
                            <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                              {insuranceGapData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.fill} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>

                  {/* Health & Emergency Fund */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-[#1A2942] rounded-lg p-4">
                      <h4 className="text-green-400 font-bold mb-3">Health Insurance Gap</h4>
                      <div className="space-y-3">
                        <div className="bg-[#0F172A] rounded-lg p-3">
                          <p className="text-gray-400 text-xs">Required Health Cover</p>
                          <p className="text-white font-bold">{formatCurrency(data.insurance.required_health_cover)}</p>
                        </div>
                        <div className="bg-[#0F172A] rounded-lg p-3">
                          <p className="text-gray-400 text-xs">Current Health Cover</p>
                          <p className="text-green-400 font-bold">{formatCurrency(data.insurance.current_health_cover || 0)}</p>
                        </div>
                        <div className="bg-[#0F172A] rounded-lg p-3 border border-yellow-500/50">
                          <p className="text-gray-400 text-xs">Health Insurance Gap</p>
                          <p className="text-yellow-400 font-bold">{formatCurrency(data.insurance.health_gap)}</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-[#1A2942] rounded-lg p-4">
                      <h4 className="text-orange-400 font-bold mb-3">Emergency Fund Analysis</h4>
                      <div className="space-y-3">
                        <div className="bg-[#0F172A] rounded-lg p-3">
                          <p className="text-gray-400 text-xs">Monthly Expenses</p>
                          <p className="text-white font-bold">{formatCurrency(data.insurance.monthly_expense)}</p>
                        </div>
                        <div className="bg-[#0F172A] rounded-lg p-3">
                          <p className="text-gray-400 text-xs">Required Emergency Fund ({data.insurance.months_required || 6} months)</p>
                          <p className="text-white font-bold">{formatCurrency(data.insurance.emergency_fund_required)}</p>
                        </div>
                        <div className="bg-[#0F172A] rounded-lg p-3 border border-yellow-500/50">
                          <p className="text-gray-400 text-xs">Emergency Fund Gap</p>
                          <p className="text-yellow-400 font-bold">{formatCurrency(data.insurance.emergency_fund_gap)}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* AI Insurance Recommendations */}
                  {(data.insurance.ai_recommendation || aiInsights.insurance) && (
                    <div className="bg-[#1A2942] rounded-lg p-4 border border-[#D4AF37]">
                      <div className="flex items-center space-x-2 mb-3">
                        <FaBrain className="text-[#FFD700]" />
                        <h5 className="text-[#FFD700] font-bold">AI-Powered Insurance Recommendations</h5>
                      </div>
                      <p className="text-gray-300 whitespace-pre-wrap text-sm">{formatAIInsights(data.insurance.ai_recommendation || aiInsights.insurance)}</p>
                    </div>
                  )}
                </>
              ) : (
                <div className="bg-[#1A2942] rounded-lg p-8 text-center">
                  <p className="text-gray-400">Insurance analysis not available. Please calculate insurance analysis in the main app.</p>
                </div>
              )}
            </div>
          </div>

          {/* SECTION 6: Goal-Based Financial Planning */}
          <div className="border-b border-[#2D5A8F]">
            <SectionHeader icon={FaPiggyBank} title="GOAL-BASED FINANCIAL PLANNING" subtitle="Plan for life's major milestones" />
            <div className="p-6 space-y-4">
              {/* Goal Type Tags */}
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 rounded-full bg-blue-500/20 text-blue-400 text-sm">🎓 Education Goals: {data?.educationGoals?.length || 0}</span>
                <span className="px-3 py-1 rounded-full bg-pink-500/20 text-pink-400 text-sm">💒 Marriage Goals: {data?.marriageGoals?.length || 0}</span>
                <span className="px-3 py-1 rounded-full bg-green-500/20 text-green-400 text-sm">🏖️ Retirement: {data?.retirementGoal ? 1 : 0}</span>
                <span className="px-3 py-1 rounded-full bg-purple-500/20 text-purple-400 text-sm">🎯 Custom Goals: {data?.customGoals?.length || 0}</span>
              </div>

              {/* Goals List */}
              {allGoals.length > 0 ? (
                <div className="space-y-4">
                  {allGoals.map((goal, idx) => (
                    <div key={idx} className="bg-[#1A2942] rounded-lg p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h4 className="text-white font-bold text-lg flex items-center space-x-2">
                            <span className="text-2xl">{goal.icon}</span>
                            <span>{goal.goal_name || goal.child_name || `${goal.type} Goal`}</span>
                          </h4>
                          <p className="text-gray-400 text-sm">
                            {goal.type} Goal
                            {goal.target_age ? ` • Target Age: ${goal.target_age}` : ''} 
                            {goal.time_horizon ? ` • Time Horizon: ${goal.time_horizon} years` : ''}
                          </p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          goal.type === 'Education' ? 'bg-blue-500/20 text-blue-400' :
                          goal.type === 'Marriage' ? 'bg-pink-500/20 text-pink-400' :
                          goal.type === 'Retirement' ? 'bg-green-500/20 text-green-400' :
                          'bg-purple-500/20 text-purple-400'
                        }`}>
                          {goal.type}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-5 gap-3">
                        <div className="bg-[#0F172A] rounded-lg p-3">
                          <p className="text-gray-400 text-xs">Future Value</p>
                          <p className="text-[#FFD700] font-bold">{formatCurrency(goal.future_value)}</p>
                        </div>
                        <div className="bg-[#0F172A] rounded-lg p-3">
                          <p className="text-gray-400 text-xs">Monthly SIP Required</p>
                          <p className="text-white font-bold">{formatCurrency(goal.monthly_sip)}</p>
                        </div>
                        <div className="bg-[#0F172A] rounded-lg p-3">
                          <p className="text-gray-400 text-xs">Expected Return</p>
                          <p className="text-white font-bold">{goal.expected_return || 12}%</p>
                        </div>
                        <div className="bg-[#0F172A] rounded-lg p-3">
                          <p className="text-gray-400 text-xs">Life Insurance Needed</p>
                          <p className="text-[#FFD700] font-bold">{formatCurrency(goal.life_insurance_needed || goal.future_value)}</p>
                        </div>
                        <div className="bg-[#0F172A] rounded-lg p-3">
                          <p className="text-gray-400 text-xs">Current Corpus</p>
                          <p className="text-green-400 font-bold">{formatCurrency(goal.current_corpus || goal.existing_corpus || 0)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-[#1A2942] rounded-lg p-8 text-center">
                  <p className="text-gray-400">No financial goals added yet</p>
                </div>
              )}
            </div>
          </div>

          {/* SECTION 7: Net Worth Snapshot */}
          <div className="border-b border-[#2D5A8F]">
            <SectionHeader icon={FaWallet} title="NET WORTH SNAPSHOT" subtitle="Comprehensive view of your financial position" />
            <div className="p-6 space-y-4">
              {data?.netWorth ? (
                <>
                  {/* Net Worth Summary with Chart */}
                  <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <div className="bg-green-500/10 border border-green-500 rounded-lg p-4">
                        <p className="text-gray-400 text-sm">Total Assets</p>
                        <p className="text-green-400 font-bold text-3xl">{formatCurrency(data.netWorth.total_assets)}</p>
                      </div>
                      <div className="bg-red-500/10 border border-red-500 rounded-lg p-4">
                        <p className="text-gray-400 text-sm">Total Liabilities</p>
                        <p className="text-red-400 font-bold text-3xl">{formatCurrency(data.netWorth.total_liabilities)}</p>
                      </div>
                      <div className="bg-[#D4AF37]/10 border-2 border-[#D4AF37] rounded-lg p-4">
                        <p className="text-gray-400 text-sm">Net Worth</p>
                        <p className="text-[#FFD700] font-bold text-3xl">{formatCurrency(data.netWorth.net_worth)}</p>
                      </div>
                    </div>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={netWorthChartData}>
                          <XAxis dataKey="name" tick={{ fill: '#9CA3AF' }} />
                          <YAxis tickFormatter={(v) => formatCurrency(v)} tick={{ fill: '#9CA3AF' }} />
                          <Tooltip formatter={(v) => formatCurrency(v)} />
                          <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                            {netWorthChartData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.fill} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Financial Ratios */}
                  <div className="bg-[#1A2942] rounded-lg p-4">
                    <h4 className="text-[#FFD700] font-bold mb-3">Financial Health Ratios</h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-[#0F172A] rounded-lg p-4 text-center">
                        <p className="text-gray-400 text-sm">Debt-to-Asset Ratio</p>
                        <p className={`font-bold text-2xl ${(data.netWorth.debt_to_asset_ratio || 0) > 50 ? 'text-red-400' : 'text-green-400'}`}>
                          {(data.netWorth.debt_to_asset_ratio || 0).toFixed(1)}%
                        </p>
                        <p className="text-gray-500 text-xs mt-1">{(data.netWorth.debt_to_asset_ratio || 0) > 50 ? 'High Debt Load' : 'Healthy Level'}</p>
                      </div>
                      <div className="bg-[#0F172A] rounded-lg p-4 text-center">
                        <p className="text-gray-400 text-sm">Savings Rate</p>
                        <p className="text-white font-bold text-2xl">{(data.netWorth.savings_rate || 0).toFixed(1)}%</p>
                        <p className="text-gray-500 text-xs mt-1">Of Annual Income</p>
                      </div>
                      <div className="bg-[#0F172A] rounded-lg p-4 text-center">
                        <p className="text-gray-400 text-sm">Liquidity Ratio</p>
                        <p className="text-white font-bold text-2xl">{(data.netWorth.liquidity_ratio || 0).toFixed(2)}</p>
                        <p className="text-gray-500 text-xs mt-1">{(data.netWorth.liquidity_ratio || 0) >= 1 ? 'Adequate' : 'Needs Improvement'}</p>
                      </div>
                    </div>
                  </div>

                  {/* AI-Powered Net Worth Analysis */}
                  {aiInsights.netWorth && (
                    <div className="bg-[#1A2942] rounded-lg p-4 border border-[#D4AF37]">
                      <div className="flex items-center space-x-2 mb-3">
                        <FaBrain className="text-[#FFD700]" />
                        <h5 className="text-[#FFD700] font-bold">AI-Powered Net Worth Analysis (Arth Gurukul AI)</h5>
                      </div>
                      <p className="text-gray-300 whitespace-pre-wrap text-sm">{formatAIInsights(aiInsights.netWorth)}</p>
                    </div>
                  )}
                </>
              ) : (
                <div className="bg-[#1A2942] rounded-lg p-8 text-center">
                  <p className="text-gray-400">Net worth analysis not available</p>
                </div>
              )}
            </div>
          </div>

          {/* SECTION 8: Executive Summary */}
          <div className="border-b border-[#2D5A8F]">
            <SectionHeader icon={FaFileAlt} title="EXECUTIVE SUMMARY" subtitle="Your complete financial snapshot at a glance" />
            <div className="p-6 space-y-4">
              {/* Total Monthly SIP Commitment Highlight */}
              <div className="bg-gradient-to-r from-[#D4AF37] to-[#FFD700] rounded-lg p-6 text-center">
                <p className="text-[#1A2942] font-semibold text-lg">Total Monthly SIP Commitment Required</p>
                <p className="text-[#1A2942] font-bold text-5xl mt-2">
                  {formatCurrency(allGoals.reduce((sum, g) => sum + (g.monthly_sip || 0), 0))}
                </p>
              </div>

              {/* Goal-wise Breakdown Table */}
              <div className="bg-[#1A2942] rounded-lg overflow-hidden">
                <h4 className="text-white font-bold p-4 bg-[#0F172A]">Goal-wise Monthly SIP Breakdown</h4>
                <table className="w-full text-sm">
                  <thead className="bg-[#0F172A]/50">
                    <tr className="text-gray-400">
                      <th className="px-4 py-3 text-left">Goal</th>
                      <th className="px-4 py-3 text-right">Target Amount</th>
                      <th className="px-4 py-3 text-right">Timeline</th>
                      <th className="px-4 py-3 text-right">Monthly SIP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allGoals.length > 0 ? allGoals.map((goal, idx) => (
                      <tr key={idx} className="border-t border-[#2D5A8F]">
                        <td className="px-4 py-3 text-white">{goal.icon} {goal.goal_name || goal.child_name || goal.type}</td>
                        <td className="px-4 py-3 text-white text-right">{formatCurrency(goal.future_value)}</td>
                        <td className="px-4 py-3 text-white text-right">{goal.time_horizon || '-'} years</td>
                        <td className="px-4 py-3 text-[#FFD700] font-bold text-right">{formatCurrency(goal.monthly_sip)}</td>
                      </tr>
                    )) : (
                      <tr><td colSpan="4" className="px-4 py-6 text-center text-gray-400">No goals added</td></tr>
                    )}
                  </tbody>
                  <tfoot className="bg-[#0F172A]">
                    <tr>
                      <td colSpan="3" className="px-4 py-3 text-white font-bold">TOTAL MONTHLY COMMITMENT</td>
                      <td className="px-4 py-3 text-[#FFD700] font-bold text-right text-lg">
                        {formatCurrency(allGoals.reduce((sum, g) => sum + (g.monthly_sip || 0), 0))}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-[#1A2942] rounded-lg p-4 text-center">
                  <p className="text-gray-400 text-sm">Total Goals</p>
                  <p className="text-white font-bold text-2xl">{allGoals.length}</p>
                </div>
                <div className="bg-[#1A2942] rounded-lg p-4 text-center">
                  <p className="text-gray-400 text-sm">Total Target Corpus</p>
                  <p className="text-[#FFD700] font-bold text-xl">{formatCurrency(allGoals.reduce((sum, g) => sum + (g.future_value || 0), 0))}</p>
                </div>
                <div className="bg-[#1A2942] rounded-lg p-4 text-center">
                  <p className="text-gray-400 text-sm">Total Life Insurance Needed</p>
                  <p className="text-blue-400 font-bold text-xl">{formatCurrency(allGoals.reduce((sum, g) => sum + (g.life_insurance_needed || g.future_value || 0), 0))}</p>
                </div>
                <div className="bg-[#1A2942] rounded-lg p-4 text-center">
                  <p className="text-gray-400 text-sm">Avg Time Horizon</p>
                  <p className="text-white font-bold text-2xl">
                    {allGoals.length > 0 ? Math.round(allGoals.reduce((sum, g) => sum + (g.time_horizon || 0), 0) / allGoals.length) : 0} yrs
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* SECTION 9: Personal Finance Wisdom */}
          <div className="border-b border-[#2D5A8F]">
            <SectionHeader icon={FaLightbulb} title="PERSONAL FINANCE WISDOM" subtitle="Essential Do's & Don'ts for a Secure Financial Future" />
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: '🛡️', title: 'Income & Debt Protection', dos: ['Buy adequate Term Life Insurance (15-20x annual income)', 'Secure a comprehensive Health Insurance policy'], donts: ["Don't mix insurance with investment (avoid ULIPs)", "Don't hide medical history from insurers"] },
                  { icon: '🚑', title: 'Emergency Fund', dos: ['Keep 6-12 months of living expenses in liquid assets', 'Replenish the fund immediately if used'], donts: ["Don't invest emergency money in volatile assets", "Don't treat emergency fund as investment"] },
                  { icon: '📊', title: 'Investments & Goals', dos: ['Link every investment to a specific financial goal', 'Start early to benefit from compounding'], donts: ["Don't stop SIPs during market corrections", "Don't chase past returns or tips"] },
                  { icon: '👴', title: 'Retirement Planning', dos: ['Prioritize retirement planning over everything', 'Maximize EPF and PPF contributions'], donts: ["Don't withdraw from retirement corpus for short-term needs", "Don't underestimate retirement expenses"] },
                  { icon: '🎓', title: "Children's Education", dos: ['Start education planning when child is young', 'Consider inflation in education costs (8-10% annually)'], donts: ["Don't underestimate future education costs", "Don't rely solely on education loans"] },
                  { icon: '💒', title: "Children's Marriage", dos: ['Plan wedding expenses 15-20 years in advance', 'Factor in cultural expectations and inflation'], donts: ["Don't take loans for wedding expenses", "Don't deplete your retirement savings for weddings"] }
                ].map((card, idx) => (
                  <div key={idx} className="bg-[#1A2942] rounded-lg p-4">
                    <h5 className="text-white font-bold mb-3 flex items-center space-x-2">
                      <span className="text-xl">{card.icon}</span>
                      <span>{card.title}</span>
                    </h5>
                    <div className="space-y-2">
                      <div>
                        <p className="text-green-400 font-bold text-sm mb-1">✅ DO's:</p>
                        {card.dos.map((d, i) => <p key={i} className="text-gray-300 text-sm pl-4">• {d}</p>)}
                      </div>
                      <div>
                        <p className="text-red-400 font-bold text-sm mb-1">❌ DON'Ts:</p>
                        {card.donts.map((d, i) => <p key={i} className="text-gray-300 text-sm pl-4">• {d}</p>)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* General Tips */}
              <div className="bg-[#1A2942] rounded-lg p-4 border border-[#D4AF37]">
                <h5 className="text-[#FFD700] font-bold mb-3">💡 General Financial Tips</h5>
                <div className="grid grid-cols-2 gap-3 text-sm text-gray-300">
                  <p>✓ Always pay yourself first (save before spending)</p>
                  <p>✓ Diversify investments across asset classes</p>
                  <p>✓ Review your financial plan at least annually</p>
                  <p>✓ Keep debt-to-income ratio below 40%</p>
                  <p>✓ Maintain a personal budget and track expenses</p>
                  <p>✓ Create a will and nominate beneficiaries</p>
                </div>
              </div>
            </div>
          </div>

          {/* SECTION 10: Next Steps & Commitment */}
          <div className="border-b border-[#2D5A8F]">
            <SectionHeader icon={FaClipboardList} title="NEXT STEPS & COMMITMENT" subtitle="Action items and acknowledgment" />
            <div className="p-6 space-y-4">
              {/* Scheduled Review Frequency */}
              {data?.nextSteps && (data?.nextSteps?.review_frequency || data?.nextSteps?.next_review_date) && (
                <div className="bg-[#1A2942] rounded-lg p-4">
                  <h4 className="text-white font-bold mb-3">Scheduled Review</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-[#0F172A] rounded-lg p-4">
                      <p className="text-gray-400 text-sm">Review Frequency</p>
                      <p className="text-[#FFD700] font-bold text-xl">{data.nextSteps.review_frequency || 'Not Set'}</p>
                    </div>
                    <div className="bg-[#0F172A] rounded-lg p-4">
                      <p className="text-gray-400 text-sm">Next Review Date</p>
                      <p className="text-white font-bold text-xl">
                        {data.nextSteps.next_review_date 
                          ? new Date(data.nextSteps.next_review_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                          : 'Not Set'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Items */}
              <div className="bg-[#1A2942] rounded-lg p-4">
                <h4 className="text-white font-bold mb-3">Recommended Action Items</h4>
                <div className="space-y-2">
                  {(data?.nextSteps?.action_items || [
                    'Start SIP investments for all identified goals immediately',
                    'Review and update insurance coverage as recommended',
                    'Build emergency fund equal to 6 months of expenses',
                    'Review portfolio allocation based on risk profile quarterly',
                    'Schedule review meetings as per agreed frequency'
                  ]).map((item, idx) => (
                    <div key={idx} className="flex items-start space-x-3 bg-[#0F172A] rounded-lg p-3">
                      <span className="text-[#FFD700] font-bold">{idx + 1}.</span>
                      <p className="text-gray-300">{item}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Signatures */}
              {(data?.nextSteps?.client_signature || data?.nextSteps?.advisor_signature) && (
                <div className="bg-[#1A2942] rounded-lg p-4">
                  <h4 className="text-white font-bold mb-3">Acknowledgment & Signatures</h4>
                  <div className="grid grid-cols-2 gap-6">
                    {data?.nextSteps?.client_signature && (
                      <div className="bg-[#0F172A] rounded-lg p-4">
                        <p className="text-gray-400 text-sm mb-2">Client Signature:</p>
                        <img src={data.nextSteps.client_signature} alt="Client Signature" className="h-20 bg-white rounded p-2" />
                        <p className="text-gray-500 text-xs mt-2">Date: {data.nextSteps.client_signed_date || new Date().toLocaleDateString()}</p>
                      </div>
                    )}
                    {data?.nextSteps?.advisor_signature && (
                      <div className="bg-[#0F172A] rounded-lg p-4">
                        <p className="text-gray-400 text-sm mb-2">Advisor Signature:</p>
                        <img src={data.nextSteps.advisor_signature} alt="Advisor Signature" className="h-20 bg-white rounded p-2" />
                        <p className="text-gray-500 text-xs mt-2">Date: {data.nextSteps.advisor_signed_date || new Date().toLocaleDateString()}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Feedback */}
              {data?.nextSteps?.client_feedback && (
                <div className="bg-[#1A2942] rounded-lg p-4">
                  <h4 className="text-white font-bold mb-2">Client Feedback</h4>
                  <p className="text-gray-300 bg-[#0F172A] rounded-lg p-3">{data.nextSteps.client_feedback}</p>
                  {data.nextSteps.rating && (
                    <div className="mt-2">
                      <span className="text-gray-400">Rating: </span>
                      <span className="text-[#FFD700]">{'⭐'.repeat(data.nextSteps.rating)}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Report Footer */}
          <div className="bg-gradient-to-r from-[#1A2942] via-[#1E3A5F] to-[#1A2942] p-6 text-center">
            <p className="text-[#FFD700] font-bold text-lg mb-2">Thank you for trusting us with your financial future!</p>
            <p className="text-gray-300 text-sm">Your Financial Advisor: {branding.advisor_name || 'Advisor'} | {branding.phone || ''} | {branding.email || ''}</p>
            <div className="mt-4 pt-4 border-t border-[#2D5A8F]">
              <p className="text-gray-500 text-xs leading-relaxed max-w-3xl mx-auto">
                <strong>Disclaimer:</strong> This report is for educational and informational purposes only. Mutual fund investments are subject to market risks; 
                please read all scheme-related documents carefully. Past performance is not indicative of future returns. The projections shown are based on 
                user inputs and certain assumptions about inflation and rates of return. Actual results may vary. Please consult with a qualified financial 
                advisor before making any investment decisions.
              </p>
              <p className="text-gray-600 text-xs mt-2">Generated on: {new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
            </div>
          </div>

        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          .print\\:hidden { display: none !important; }
          .print\\:overflow-visible { overflow: visible !important; }
          .print\\:bg-white { background-color: white !important; }
          .print\\:max-w-none { max-width: none !important; }
          .print\\:relative { position: relative !important; }
        }
      `}</style>
    </div>
  );
};

export default PDFReportPreview;
